import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getRetentionSettings,
  purgeActivityLogs,
  runAutoPurgeIfDue,
  saveRetentionSettings,
  type RetentionSettings,
} from '@/lib/activityLogRetention';

export function useActivityLogRetention(enabled: boolean, onPurged?: () => void) {
  const [settings, setSettings] = useState<RetentionSettings>(() => getRetentionSettings());
  const [purging, setPurging] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const onPurgedRef = useRef(onPurged);

  useEffect(() => {
    onPurgedRef.current = onPurged;
  });

  const update = useCallback((patch: Partial<RetentionSettings>) => {
    setSettings(saveRetentionSettings(patch));
  }, []);

  const dismiss = useCallback(() => {
    setMessage(null);
    setError(null);
  }, []);

  const runPurge = useCallback(async (months: number | null): Promise<number | null> => {
    setPurging(true);
    setError(null);
    setMessage(null);
    try {
      const { deleted, expected } = await purgeActivityLogs(months);
      setSettings(saveRetentionSettings({ lastRunAt: new Date().toISOString() }));

      if (expected > 0 && deleted === 0) {
        setError(
          'La base de datos bloqueó el borrado: falta habilitar la función de limpieza para administradores. Mientras tanto los registros no se pueden eliminar.'
        );
        return null;
      }

      setMessage(
        deleted === 0
          ? 'No había registros de éxito o aviso que vaciar.'
          : `Se eliminaron ${deleted} registros de éxito y aviso. Los errores quedaron intactos.`
      );
      onPurgedRef.current?.();
      return deleted;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo vaciar el registro.');
      return null;
    } finally {
      setPurging(false);
    }
  }, []);

  // Limpieza automática mensual (una sola vez cada 30 días).
  useEffect(() => {
    if (!enabled) return;
    let alive = true;

    const run = async () => {
      try {
        const result = await runAutoPurgeIfDue();
        if (!alive || !result.ran) return;
        setSettings(getRetentionSettings());
        if (result.deleted > 0) {
          setMessage(
            `Limpieza automática mensual: se eliminaron ${result.deleted} registros de éxito y aviso.`
          );
          onPurgedRef.current?.();
        }
      } catch {
        // El mantenimiento nunca debe romper la aplicación.
      }
    };

    void run();
    return () => {
      alive = false;
    };
  }, [enabled]);

  return { settings, update, purging, message, error, runPurge, dismiss };
}